import { Badge, type DataTableColumn } from "@vjal/ui";
import { humanizeStatus } from "../lib/status-format";
import { CohortsPage } from "./cohorts/cohorts-page";
import { MyCohortsPage } from "./cohorts/my-cohorts-page";
import { LiveCohortCreateForm } from "./product-live-create-forms";
import type { ProductScreenContract, ProductScreenContext, TableRow } from "./product-route-contracts";

// ── Cohorts (Wave 3 — REBUILT as gold-pattern per-screen pages) ────────────
// The thin contract-surface cohort workspace was replaced by real per-screen
// pages on the gold pattern: a CohortsPage list (DataTable + filter + role-gated
// create/edit/complete modals with EDITABLE company/status/program dropdowns —
// FR-COHORT-001 + cohort-side program assignment), a MyCohortsPage scoped
// read-only list (coach/participant), and a CohortDetailPage with REAL URL-driven
// tab routing (Overview / Participants / Sessions / Invites / Attendance /
// Progress / Reports / Recordings / Assignments). Each routes through
// useHonestQuery -> <HonestState>. The contract factories below preserve the
// route registration + provenance metadata and render the new pages via
// `customBody`.
//
// PROGRAM ASSIGNMENT (the Wave-2 deferral, completed here): the cohort carries a
// single program_id; the cohort form's Program Select (active programs only) IS
// the program -> cohort assignment. There is NO separate assign endpoint.
//
// PARTICIPANT is DENIED THE COHORTS OPERATIONAL MODULE: the route manifest's
// `cohorts` route uses cohortReadRoles (super_admin/admin/coach/company_admin/
// cohort_lead), which EXCLUDES participant — so the route is generic-not-found
// for them and the Cohorts nav item is absent. Participants reach their OWN
// cohorts only through My Cohorts (roles:["coach","participant"]).
//
// The Reports detail tab is REPORT-EXCLUDED (status only) for every role.

const cohortRows: TableRow[] = [
  {
    id: "cohort-1",
    cohort: "Spring Leaders",
    company: "Acme Leadership",
    program: "Executive Coaching",
    coach: "Coach One",
    status: "active",
    participants: "8 active"
  }
];

const learnerRows: TableRow[] = [
  { id: "cohort-1", cohort: "Spring Leaders", company: "Acme Leadership", program: "Executive Coaching", status: "active" }
];


function focusedDefaults(): Pick<
  ProductScreenContract,
  "requiredViewports" | "surfaceMode" | "closureState"
> {
  return {
    requiredViewports: ["desktop", "mobile"],
    surfaceMode: "focused",
    closureState: "focused",
  };
}

export function cohortListScreen(): ProductScreenContract {
  return {
    title: () => "Cohorts",
    primaryJob:
      "Review every cohort in scope — search, filter by status, and (for staff/coach) create, edit, or complete — with editable company, status, and program dropdowns.",
    secondaryJob:
      "Company admin sees company cohorts read-only; cohort lead sees assigned cohorts read-only; participant has no Cohorts route (My Cohorts only).",
    workTitle: "Cohort directory",
    rows: cohortRows,
    columns: cohortColumns(),
    states: ["loading", "empty", "permission-denied", "unavailable", "error", "read-only"],
    apiFamilies: ["cohort-service", "company-service", "program-service", "user-service"],
    roleDeltas:
      "Super admin/admin/coach can create/edit/complete cohorts and edit company/status/program; company_admin and cohort_lead are read-only; participant is route-denied.",
    extensionSlots: ["cohort completion", "coach reassignment", "event-history entry"],
    mobilePosture: "Cohort filter bar, table rows, and lifecycle controls stack with no horizontal overflow.",
    evidenceRequirement:
      "Playwright must prove staff/coach create/edit/complete with editable dropdowns, read-only roles, participant route-denied with no Cohorts nav, and mobile no-overflow.",
    surfaceMode: "focused",
    customBody: CohortsPage
  };
}

export function learnerCohortsScreen(): ProductScreenContract {
  return {
    title: () => "My cohorts",
    primaryJob: "Show the caller's own scoped cohorts (coach: assigned; participant: enrolled) as a read-only list.",
    secondaryJob: "Read-only — no operational controls; the server pins the scope and the frontend never widens it.",
    workTitle: "My cohorts",
    rows: learnerRows,
    columns: cohortColumns(),
    states: ["loading", "empty", "permission-denied", "unavailable", "error"],
    apiFamilies: ["cohort-service"],
    roleDeltas: "Coach sees assigned cohorts; participant sees own cohorts; every other role is route-denied.",
    extensionSlots: ["own report shortcuts", "attendance summary"],
    mobilePosture: "Own cohort rows remain readable in a single column.",
    evidenceRequirement: "Playwright must prove coach assigned-only and participant own-only scope with no write controls.",
    surfaceMode: "focused",
    customBody: MyCohortsPage
  };
}

export function cohortCreateScreen(): ProductScreenContract {
  return {
    ...cohortListScreen(),
    title: () => "Create cohort",
    primaryJob:
      "Create a cohort with name, company (editable dropdown), program (active-program assignment), coach, and status.",
    secondaryJob: "Creation is blocked for read-only roles; the create form opens on this route for authorized roles.",
    workTitle: "Create cohort",
    // F3: route-mounted Form-1 via LiveFormModule → FormSurface (not CohortsPage list).
    ...focusedDefaults(),
    customBody: CohortCreateSurface
  };
}

function CohortCreateSurface({ role }: ProductScreenContext) {
  return (
    <section aria-label="Create cohort" className="col-span-12" data-testid="cohort-create-page">
      <LiveCohortCreateForm role={role} />
    </section>
  );
}

function cohortColumns(): DataTableColumn<TableRow>[] {
  return [
    { key: "cohort", header: "Cohort" },
    { key: "company", header: "Company" },
    { key: "program", header: "Program" },
    { key: "coach", header: "Coach" },
    { key: "status", header: "Status", render: (row) => <StatusBadge value={String(row.status)} /> }
  ];
}

function StatusBadge({ value }: { value: string }) {
  return <Badge variant={value === "active" ? "success" : value === "draft" ? "warning" : "default"}>{humanizeStatus(value)}</Badge>;
}
